import React, { useState, useEffect, useCallback } from 'react';
import { getAdminUsers, addUserCredits, updateUserSubscription, updateUserRole, toggleUserSuspend, deleteUser } from '../services/api';
import { useLang } from '../i18n/LanguageContext';
import { useAuth } from '../auth/AuthContext';

export default function AdminUsersPage() {
  const { lang, t } = useLang();
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  // Modal state
  const [modal, setModal] = useState(null); // { type, userId, userName }
  const [creditsAmount, setCreditsAmount] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAdminUsers();
      setUsers(data);
    } catch {
      setError('Failed to load users.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = users.filter(u =>
    u.fullName.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleAddCredits = async () => {
    const amount = parseInt(creditsAmount);
    if (!amount || amount <= 0) return;
    setActionLoading(modal.userId);
    try {
      await addUserCredits(modal.userId, amount);
      await loadUsers();
      refreshUser();
      setModal(null);
      setCreditsAmount('');
    } catch { setError('Failed to add credits.'); }
    setActionLoading('');
  };

  const handleUpdateSubscription = async () => {
    if (!selectedSubscription) return;
    setActionLoading(modal.userId);
    try {
      await updateUserSubscription(modal.userId, selectedSubscription);
      await loadUsers();
      refreshUser();
      setModal(null);
    } catch { setError('Failed to update subscription.'); }
    setActionLoading('');
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;
    setActionLoading(modal.userId);
    try {
      await updateUserRole(modal.userId, selectedRole);
      await loadUsers();
      refreshUser();
      setModal(null);
    } catch { setError('Failed to update role.'); }
    setActionLoading('');
  };

  const handleToggleSuspend = async (userId) => {
    setActionLoading(userId);
    try {
      await toggleUserSuspend(userId);
      await loadUsers();
      refreshUser();
    } catch { setError('Failed to update suspension.'); }
    setActionLoading('');
  };

  const handleDelete = async (userId) => {
    setActionLoading(userId);
    try {
      await deleteUser(userId);
      await loadUsers();
    } catch { setError('Failed to delete user.'); }
    setActionLoading('');
  };

  const openModal = (type, user) => {
    setModal({ type, userId: user.id, userName: user.fullName });
    if (type === 'subscription') setSelectedSubscription(user.subscriptionStatus);
    if (type === 'role') setSelectedRole(user.role);
    if (type === 'credits') setCreditsAmount('');
  };

  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const subscriptionBadge = (status) => {
    const colors = {
      'Premium+': 'badge-premium-plus',
      'Premium': 'badge-premium',
      'Free': 'badge-free',
    };
    return colors[status] || 'badge-free';
  };

  const inputClasses = "w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#FA6868] focus:border-[#FA6868] transition-shadow hover:border-gray-300 shadow-sm bg-white text-gray-800";
  const btnPrimary = "px-5 py-2.5 text-sm font-semibold bg-[#FA6868] text-white rounded-lg hover:bg-[#e65b5b] disabled:opacity-50 transition-colors shadow-sm cursor-pointer";

  return (
    <div className="max-w-[1100px] w-full mx-auto px-6 py-8 pb-12" dir={dir}>
      <div className="bg-surface border border-border rounded-xl shadow-soft p-6 md:p-8 mt-4">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
            <span className="material-symbols-outlined text-[24px] text-[#FA6868]">group</span>
            <h1 className="text-xl font-bold text-gray-800">{t.adminUsersTitle}</h1>
          </div>
          <p className="text-sm text-[#64748b] ml-2">{t.adminUsersSubtitle}</p>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 cursor-pointer">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-50 rounded-xl p-4 border border-border">
            <div className="text-3xl font-bold text-gray-800">{users.length}</div>
            <div className="text-xs font-semibold text-[#64748b] mt-1 uppercase tracking-wide">Total Users</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-border">
            <div className="text-3xl font-bold text-emerald-500">{users.filter(u => !u.isSuspended).length}</div>
            <div className="text-xs font-semibold text-[#64748b] mt-1 uppercase tracking-wide">{t.adminActive}</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-border">
            <div className="text-3xl font-bold text-[#FA6868]">{users.filter(u => u.isSuspended).length}</div>
            <div className="text-xs font-semibold text-[#64748b] mt-1 uppercase tracking-wide">{t.adminSuspended}</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-border">
            <div className="text-3xl font-bold text-[#fbbf24]">{users.reduce((sum, u) => sum + u.imagesGenerated, 0)}</div>
            <div className="text-xs font-semibold text-[#64748b] mt-1 uppercase tracking-wide">{t.adminUserImages}</div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
          <div className="relative w-full md:w-96">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b] text-[20px]">search</span>
            <input
              type="text"
              placeholder={t.adminSearchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={`${inputClasses} pl-10`}
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#FA6868] rounded-full animate-spin" />
            <p className="text-[#64748b] mt-4 text-sm">Loading users...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-border text-[#64748b]">{t.adminNoUsers}</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-border text-[#64748b] text-[11px] font-semibold uppercase tracking-wide">
                  <th className="text-left py-4 px-4">{t.adminUserName}</th>
                  <th className="text-left py-4 px-4">{t.adminUserEmail}</th>
                  <th className="text-center py-4 px-4">{t.adminUserRole}</th>
                  <th className="text-center py-4 px-4">{t.adminUserCredits}</th>
                  <th className="text-center py-4 px-4">{t.adminUserSubscription}</th>
                  <th className="text-center py-4 px-4">{t.adminUserImages}</th>
                  <th className="text-center py-4 px-4">{t.adminUserStatus}</th>
                  <th className="text-center py-4 px-4">{t.adminUserActions}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => {
                  const isCurrentUser = u.id === currentUser?.id;
                  const isLoading = actionLoading === u.id;
                  return (
                    <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-[#fbbf24] flex items-center justify-center text-xs font-bold text-white shrink-0 shadow-sm">
                            {u.fullName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <span className="font-semibold text-gray-800">{u.fullName}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-[#64748b] font-medium">{u.email}</td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => !isCurrentUser && openModal('role', u)}
                          disabled={isCurrentUser}
                          className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${
                            u.role === 'admin' ? 'bg-[#fbbf24]/10 text-[#d97706] border-[#fbbf24]/30' : 'bg-gray-100 text-[#64748b] border-gray-200'
                          } ${!isCurrentUser ? 'hover:ring-1 hover:ring-[#FA6868] cursor-pointer transition-all' : 'opacity-70'}`}
                        >
                          {u.role}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => openModal('credits', u)}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-[#FA6868] cursor-pointer transition-colors bg-gray-50 px-2.5 py-1 rounded-md border border-gray-200"
                        >
                          <span className="material-symbols-outlined text-[16px] text-[#fbbf24]">toll</span>
                          {u.credits}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => openModal('subscription', u)}
                          className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-all ${subscriptionBadge(u.subscriptionStatus)} hover:ring-1 hover:ring-[#FA6868] cursor-pointer`}
                        >
                          {u.subscriptionStatus}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-[#64748b] bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                          <span className="material-symbols-outlined text-[14px]">image</span>
                          {u.imagesGenerated}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-[11px] px-2.5 py-1 rounded-md font-bold uppercase tracking-wider ${
                          u.isSuspended ? 'bg-red-50 text-[#FA6868] border border-red-100' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                        }`}>
                          {u.isSuspended ? t.adminSuspended : t.adminActive}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {!isCurrentUser && (
                            <>
                              <button
                                onClick={() => {
                                  const msg = u.isSuspended ? t.adminConfirmUnsuspend : t.adminConfirmSuspend;
                                  if (window.confirm(msg)) handleToggleSuspend(u.id);
                                }}
                                disabled={isLoading}
                                className={`p-1.5 rounded-lg transition-colors cursor-pointer border ${
                                  u.isSuspended
                                    ? 'text-emerald-500 border-emerald-100 hover:bg-emerald-50'
                                    : 'text-amber-500 border-amber-100 hover:bg-amber-50'
                                }`}
                                title={u.isSuspended ? t.adminUnsuspend : t.adminSuspend}
                              >
                                <span className="material-symbols-outlined text-[18px]">
                                  {u.isSuspended ? 'lock_open' : 'lock'}
                                </span>
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(t.adminConfirmDelete)) handleDelete(u.id);
                                }}
                                disabled={isLoading}
                                className="p-1.5 rounded-lg text-[#FA6868] border border-red-100 hover:bg-red-50 transition-colors cursor-pointer"
                                title={t.adminDelete}
                              >
                                <span className="material-symbols-outlined text-[18px]">delete</span>
                              </button>
                            </>
                          )}
                          {isCurrentUser && (
                            <span className="text-xs font-semibold text-[#64748b] bg-gray-100 px-3 py-1 rounded-md border border-gray-200">You</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal Overlay */}
        {modal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]" onClick={() => setModal(null)}>
            <div className="bg-surface rounded-2xl border border-border shadow-2xl w-[90%] max-w-sm p-6" onClick={e => e.stopPropagation()} style={{ animation: 'fadeIn 0.2s ease' }}>

              {/* Add Credits */}
              {modal.type === 'credits' && (
                <>
                  <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#fbbf24]">toll</span>
                    {t.adminAddCredits}
                  </h3>
                  <p className="text-sm font-medium text-[#64748b] mb-5">{modal.userName}</p>
                  <input
                    type="number"
                    min="1"
                    value={creditsAmount}
                    onChange={e => setCreditsAmount(e.target.value)}
                    placeholder="Enter amount..."
                    className={`${inputClasses} mb-6`}
                    autoFocus
                  />
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setModal(null)} className="px-5 py-2.5 text-sm font-semibold text-[#64748b] hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">Cancel</button>
                    <button
                      onClick={handleAddCredits}
                      disabled={!creditsAmount || parseInt(creditsAmount) <= 0}
                      className={btnPrimary}
                    >
                      {t.adminAddCredits}
                    </button>
                  </div>
                </>
              )}

              {/* Update Subscription */}
              {modal.type === 'subscription' && (
                <>
                  <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#FA6868]">workspace_premium</span>
                    {t.adminUserSubscription}
                  </h3>
                  <p className="text-sm font-medium text-[#64748b] mb-5">{modal.userName}</p>
                  <select
                    value={selectedSubscription}
                    onChange={e => setSelectedSubscription(e.target.value)}
                    className={`custom-select ${inputClasses} mb-6`}
                  >
                    <option value="Free">Free</option>
                    <option value="Premium">Premium</option>
                    <option value="Premium+">Premium+</option>
                  </select>
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setModal(null)} className="px-5 py-2.5 text-sm font-semibold text-[#64748b] hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">Cancel</button>
                    <button onClick={handleUpdateSubscription} className={btnPrimary}>Update</button>
                  </div>
                </>
              )}

              {/* Update Role */}
              {modal.type === 'role' && (
                <>
                  <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#FA6868]">manage_accounts</span>
                    {t.adminUserRole}
                  </h3>
                  <p className="text-sm font-medium text-[#64748b] mb-5">{modal.userName}</p>
                  <select
                    value={selectedRole}
                    onChange={e => setSelectedRole(e.target.value)}
                    className={`custom-select ${inputClasses} mb-6`}
                  >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setModal(null)} className="px-5 py-2.5 text-sm font-semibold text-[#64748b] hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">Cancel</button>
                    <button onClick={handleUpdateRole} className={btnPrimary}>Update</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}